import 'firebase/compat/auth'

import { ActionContext, Module } from 'vuex'
import { AuthState, RootState } from './state_models'
import { getDecodedJWT, isJwtTokenExpired, setJWT } from '../util/jwt_helper'
import { handleMarketplaceRedirect, isMarketplaceRedirect, setMarketplaceCookie } from '../util/marketplace_helper'

import { AuthUser } from '../models'
import { IAuthToken } from '../models/auth_user'
import { OauthService } from '@/services'
import Vue from 'vue'
import axios from 'axios'
import config from '../config'
import firebase from 'firebase/compat/app'
import localStorage from 'store'
import moment from 'moment'
import restAgentV1 from '../restAgent'
import router from '@/routes/index'
import LogsService from '../services/LogsService'
import { getCookie, deleteCookie } from '../util/cookies'
import { trackPendoEvent } from '../util/helper'

export const auth: Module<AuthState, RootState> = {
  namespaced: true,
  state: {
    user: undefined,
    locationId: undefined as undefined | string,
    isUserSwitching: false,
    isTokenRefreshing: false,
    inactivityLogout: false
  },
  getters: {
    getAuth: state => {
      return state.user
    },
    isProxyUser: state => {
      if (!state?.user?.authToken) {
        return false
      }
      const jwt = getDecodedJWT(state?.user?.authToken || '') as IAuthToken
      return jwt.primaryAuthClassId != jwt.authClassId
    },
    isJwtExist: state => {
      return !!state.user?.authToken && !!state.user?.refreshToken
    },
    isUserSwitching: state => {
      return state.isUserSwitching
    },
    isTokenRefreshing: state => {
      return state.isTokenRefreshing
    },
    isInactivityLogout: state => {
      return state.inactivityLogout
    },
    isSupportAgent: state => {
      if (!state?.user?.authToken) return false
      const jwt = getDecodedJWT(state?.user?.authToken || '') as IAuthToken
      return !!jwt?.appMeta?.supportAgentId
    },
  },
  mutations: {
    setLocationId: (state: AuthState, locationId: string) => {
      state.locationId = locationId
    },
    set: (state: AuthState, payload: AuthUser) => {
      state.user = payload
    },
    delete: (state: AuthState) => {
      state.user = undefined
    },
    setUserSwitching: (state: AuthState, isSwitching: boolean) => {
      state.isUserSwitching = isSwitching
    },
    setTokenRefreshing: (state: AuthState, isRefreshing: boolean) => {
      state.isTokenRefreshing = isRefreshing
    },
    updateInactivityLogout: (state: AuthState, inactivityLogout: boolean) => {
      state.inactivityLogout = inactivityLogout
    }
  },
  actions: {
    refreshFirebaseToken: async (
      context: ActionContext<AuthState, RootState>,
      params: { [key: string]: any }
    ) => {
      //
      if (
        !params.locationId ||
        (context.state.locationId &&
          params.locationId === context.state.locationId && !params.refresh)
      ) {
        return
      }

      context.commit('setLocationId', params.locationId)
      const {
        activeLocations,
        isChanged
      } = await context.dispatch(
        'locations/addToActiveLocation',
        params.locationId && typeof params.locationId === 'string'
          ? [params.locationId]
          : params.locationId,
        { root: true }
      )
      if (!params.refresh && !isChanged) {
        return
      }

      // To capture initial token state
      let oldFirebaseToken = ''
      let locationsBefore: string[] = []

      try {
        const firebaseToken = await firebase?.auth()?.currentUser?.getIdToken() as string
        const decodedPayload = getDecodedJWT(firebaseToken)
        // Capture initial token state
        locationsBefore = decodedPayload?.locations || []
        oldFirebaseToken = firebaseToken
        if(decodedPayload?.locations?.includes(params.locationId)) {
          console.log('skipping refresh token api call [reason: duplicate]')
          return
        }
      } catch (e) {
        console.error(e)
      }

      try {
        let currentRequestTimestamp = Date.now()
        const response = await OauthService.signinRefresh(activeLocations)
        // storing token refresh timestamp to handle race condition
        // older request should never overwrite the current token
        let lastRefreshTimestamp = localStorage.get('tokenRefreshTimestamp')
        const isInvalidRequest = lastRefreshTimestamp && lastRefreshTimestamp > currentRequestTimestamp
        if (response && !isInvalidRequest) {
          localStorage.set('tokenRefreshTimestamp', currentRequestTimestamp)
          await firebase.auth().signInWithCustomToken(response.token)
          localStorage.set('refreshedToken', response.token)
          restAgentV1.setApiKey(response.token)
        }

        // Observability: Log token refresh result and locations for analysis
        try {
          const newFirebaseToken = await firebase?.auth()?.currentUser?.getIdToken() as string
          const newPayload = getDecodedJWT(newFirebaseToken)
          const locationsAfter = newPayload?.locations || []

          // prepare locationsRequestedToAdd for logging
          let locationsRequestedToAdd = params.locationId
          if (params.locationId && typeof params.locationId === 'string') {
            locationsRequestedToAdd = [params.locationId]
          }

          // prepare activeLocations for logging
          let activeLocationsToLog = activeLocations
          if (activeLocations && typeof activeLocations === 'string') {
            activeLocationsToLog = activeLocations.split(',').filter(Boolean)
          }

          /**
           * Now we will check if the new firebase token has all the locations requested via param and sent via api
           * What to log from this:
           * 1. Whether new token has all the locations requested via param and sent via api
           * 2. If not all locations are in new token, then log the locations that are not in new token
           *
           * We need to use locationsAfter to check if all locations are in new token
           */
          const locationsNotInNewTokenFromParams = locationsRequestedToAdd.filter(location => !locationsAfter.includes(location))
          const locationsNotInNewTokenFromActiveLocations = activeLocationsToLog.filter(location => !locationsAfter.includes(location))

          // We will also log which locations are missing from activeLocations when we passed params.locationId
          const locationsNotInActiveLocationsFromParams = locationsRequestedToAdd.filter(location => !activeLocationsToLog.includes(location))

          const loggerPayload = {
            event: 'firebase-token-refresh',
            oldFirebaseToken,
            newFirebaseToken,
            locationsBefore,
            locationsAfter,
            locationsRequestedToAdd,
            activeLocations,
            locationsNotInNewTokenFromParams,
            locationsNotInNewTokenFromActiveLocations,
            locationsNotInActiveLocationsFromParams
          }

          // Check if we need to log based on various conditions:
          // 1. Not all requested locations are in new token
          // 2. Not all active locations are in new token
          // 3. Some locations from params are not in new token
          // 4. Some active locations are not in new token
          // 5. Some requested locations are not in active locations
          if(
            locationsNotInNewTokenFromParams.length > 0 ||
            locationsNotInNewTokenFromActiveLocations.length > 0 ||
            locationsNotInActiveLocationsFromParams.length > 0
          ) {
            await LogsService.dump('observability-firebase-token-v2', loggerPayload)
          }
        } catch (logError) {
          console.error('Failed to log firebase token refresh event:', logError)
        }
      } catch (e) {
        console.error(`Error in refreshing firebase token : ${JSON.stringify(e)}`)
      }
    },
    get: (context: ActionContext<AuthState, RootState>) => {
      return new Promise((resolve, reject) => {
        if (context.state.user) {
          return resolve(context.state.user)
        }
        const b64String = localStorage.get('a')
        if (!b64String) return reject('The user is not logged in. user_not_logged_in')

        const user = JSON.parse(atob(b64String))
        if (user?.authToken && !isJwtTokenExpired(user.authToken)) {
          setMarketplaceCookie(user.authToken)
        }

        if (window.location.search) {
          const urlObj = new URL(window.location.href)
          const redirectParam = urlObj.searchParams.get("redirect")
          if (redirectParam && user?.authToken && !isJwtTokenExpired(user.authToken)) {
            handleMarketplaceRedirect(redirectParam)
          }
        }
        context.commit('set', user)
        setJWT(user?.jwt, user?.authToken)
        return resolve(user)
      })
    },
    setTokenRefreshing: (context: ActionContext<AuthState, RootState>, isTokenRefreshing: boolean) => {
      context.commit('setTokenRefreshing', isTokenRefreshing)
    },
    set: (context: ActionContext<AuthState, RootState>, payload: AuthUser) => {
      context.commit('set', payload)
      localStorage.set('a', btoa(JSON.stringify(payload)))
      localStorage.set('loginDate', moment().toDate())

      const leadSourceCookie = getCookie('lead_source')
      if (leadSourceCookie === 'jobber') {
        trackPendoEvent('New Jobber GHL agency', {
          timestamp: new Date().toISOString(),
          source: 'login_successful'
        })
        
        // Delete the cookie after sending the event
        deleteCookie('lead_source')
      }

      const hostName = window.location.hostname.split('.')
      const hostLength = hostName.length
      let domainName = 'localhost'
      if (hostName[0] !== 'localhost') {
        domainName = `.${hostName[hostLength - 2]}.${hostName[hostLength - 1]}`
        if(hostName.includes("gohighlevel") || (hostName.includes("leadconnectorhq"))){
          const reducedPayload = (({ apiKey, userId, companyId }) => ({ apiKey, userId, companyId }))(payload)
          document.cookie = `a=${btoa(
            JSON.stringify(reducedPayload)
          )};domain=${domainName};path=/;max-Age=31536000;secure;`
        }
        setMarketplaceCookie(payload?.authToken)

        if(window.location.search.includes('redirect=')) {
          const redirectParam = new URLSearchParams(window.location.search).get('redirect')
          if(redirectParam && payload?.authToken) {
            handleMarketplaceRedirect(redirectParam)
          }
        }
      }
      setJWT(payload?.jwt, payload?.authToken)
    },
    switchToMasterUser: {
      handler: async (context: ActionContext<AuthState, RootState>) => {
        try {
            context.commit('setUserSwitching', true)
            context.commit('setAgencyLoaderState',true,{root: true})
            const authUser = await context.dispatch('auth/get', null, { root: true })
            const signedInUserData = authUser

            setJWT(signedInUserData.jwt, signedInUserData.authToken, true)
            const data = await OauthService.getCurrentUser()
            const authSetData = {
                firebaseToken: data.token,
                jwt: data.jwt,
                refreshJwt: data.refreshJwt,
                apiKey: data.apiKey,
                userId: data.userId,
                companyId: data.companyId,
                authToken: data.authToken,
                refreshToken: data.refreshToken
            }

            /* Log out User*/
            await context.dispatch('logout', null, { root: true })
            await firebase.auth().signOut()

            /* switch back to logged user*/
            await context.dispatch('auth/set', authSetData, { root: true });
            await firebase.auth().signInWithCustomToken(authSetData.firebaseToken)
            await context.dispatch('auth/set', authSetData, { root: true });


            await context.dispatch('init', null, { root: true })
            await context.dispatch('user/get', null, { root: true })
            await context.dispatch('company/get', null, { root: true })

            await context.dispatch(
                'auth/refreshFirebaseToken',
                { locationId: router.currentRoute?.params?.location_id || null, refresh: true },
                { root: true }
            );
            localStorage.set('proxyLoginCount', (localStorage.get('proxyLoginCount') || 0)+1)
            window.location.reload()
        } catch (error) {
            console.error('something went wrong while swicthing', error)
            window.location.reload();
        }finally {
          context.commit('setAgencyLoaderState',false,{root: true})
        }
      }
    },
    logout: {
      root: true,
      handler: async (context: ActionContext<AuthState, RootState>) => {
        localStorage.remove('app_referer')
        context.commit('delete')
        localStorage.remove('a')
        localStorage.remove('loginDate')
        if ( window.pendo) {
          window.pendo.clearSession();
        }

        document.cookie =
          'a=; Max-Age=-99999999;domain=.gohighlevel.com;path=/;'

        document.cookie =
          'a=; Max-Age=-99999999; domain=.leadconnectorhq.com; path=/;'

        if (window.google && google.accounts) {
          google.accounts.id?.disableAutoSelect();
        }

        window.localStorage.clear()
        window.sessionStorage.clear()
        document.cookie.split(";").forEach(function (c) { document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); });

      }
    },
    logoutSupportAgent: {
      root: true,
      handler: async (context: ActionContext<AuthState, RootState>) => {
        window.localStorage.clear()
        window.sessionStorage.clear()
        document.cookie.split(";").forEach(function (c) { document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); });

        context.commit('delete')

        if ( window.pendo) {
          window.pendo.clearSession();
        }
      }
    },
    changeUserSwitching(context: ActionContext<AuthState, RootState>, isSwitching) {
      context.commit('setUserSwitching', isSwitching)
    },
    async signOutFromBackend(context: ActionContext<AuthState, RootState>) {
      try {
        if(localStorage.get('a')) {
          await OauthService.signout(context.state.user?.apiKey)
        } else {
          console.log('user already logged out')
        }
      } catch (error) {
        console.error(error)
      }
    },
    forceInactiveLogout: {
      handler: (context: ActionContext<AuthState, RootState>, inactiveLogout) => {
        context.commit('updateInactivityLogout', inactiveLogout)
      }
    }
  }
}
